import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import NotificationsBell from "./NotificationsBell";
import { linkNativeToBackend } from "../lib/native";

const NavLink = ({ to, children, testid }) => {
  const loc = useLocation();
  const active = loc.pathname === to;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`font-mono-eyebrow editorial-link ${active ? "text-[color:var(--accent)]" : "text-black/80 hover:text-black"}`}
    >
      {children}
    </Link>
  );
};

const ADMIN_EMAILS = ["jean@atelier.co"];

const Nav = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [billing, setBilling] = useState(null);
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    if (!user) { setBilling(null); setDigest(null); return; }
    api.get("/billing/status").then((r) => setBilling(r.data)).catch(()=>{});
    api.get("/digest/status").then((r) => setDigest(r.data)).catch(()=>{});
    // Hook up native RevenueCat linkage (no-op on web)
    linkNativeToBackend(api, user.id).catch(()=>{});
  }, [user]);

  const showTrialStrip = user && billing && billing.status === "trialing";
  const showDigestStrip = user && digest && digest.show_nudge && !showTrialStrip;
  const isAdmin = user && ADMIN_EMAILS.includes((user.email || "").toLowerCase());

  return (
    <>
      {showTrialStrip && (
        <div className="bg-black text-[#F6F5F1] text-center py-2 text-xs tracking-widest uppercase" data-testid="trial-banner">
          <span className="mr-3 opacity-70">Trial</span>
          <span>{billing.trial_days_left} day{billing.trial_days_left===1?"":"s"} left</span>
          <Link to="/pricing" className="ml-4 underline underline-offset-4 hover:text-[color:var(--accent)]" data-testid="trial-banner-upgrade">Continue for £9.99 / year →</Link>
        </div>
      )}
      {showDigestStrip && (
        <div className="bg-[#F1EFE7] text-black text-center py-2 text-xs tracking-widest uppercase border-b border-black/10" data-testid="digest-banner">
          <span className="mr-2 opacity-60">This week</span>
          <span>{digest.tryons_this_week} tryons · {digest.outfits_this_week} looks saved</span>
          <Link to="/digest" className="ml-4 underline underline-offset-4 hover:text-[color:var(--accent)]" data-testid="digest-banner-open">Read your digest →</Link>
        </div>
      )}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F6F5F1]/70 border-b border-black/5">
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <span className="w-2 h-2 rounded-full bg-[color:var(--accent)] inline-block" />
            <span className="font-serif-display text-2xl tracking-tight">Atelier<span className="text-[color:var(--accent)]">.</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {user && <NavLink to="/studio" testid="nav-studio">Studio</NavLink>}
            {user && <NavLink to="/wardrobe" testid="nav-wardrobe">Wardrobe</NavLink>}
            {user && <NavLink to="/outfits" testid="nav-outfits">Archive</NavLink>}
            {user && <NavLink to="/digest" testid="nav-digest">This Week</NavLink>}
            <NavLink to="/discover" testid="nav-discover">Discover</NavLink>
            {user && <NavLink to="/profile" testid="nav-profile">Silhouette</NavLink>}
            <NavLink to="/pricing" testid="nav-pricing">Pricing</NavLink>
            {isAdmin && <NavLink to="/admin" testid="nav-admin">Admin</NavLink>}
          </nav>
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <NotificationsBell/>
                <span className="hidden sm:inline font-mono-eyebrow">{user.name}</span>
                <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="btn-outline-ink">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/auth" data-testid="signin-btn" className="font-mono-eyebrow editorial-link">Sign in</Link>
                <Link to="/auth?mode=signup" data-testid="cta-join" className="btn-ink">Join · 30 days free</Link>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Nav;
